import {
  useGetCircuit,
  useGetDistributorEditableCircuit,
} from '@/hooks/useCircuits';
import { useFindProject } from '@/hooks/useProject';
import {
  CreateEstimateFormData,
  EstimateParticipantFormData,
} from '@/types/estimates/CreateEstimateFormData';
import {
  Control,
  SubmitHandler,
  useController,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import Loader from '../ui/Loader';
import { ProjectResponse } from '@/types/projects/ProjectResponse';
import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import StyledInput from '../styled/StyledInput';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeCircuitStartingPrice } from '@/utils/circuits/prices/computeCircuitStartingPrice';
import { Label } from '../ui/label';
import StyledBooleanCheckbox from '../styled/StyledBooleanCheckbox';
import StyledButton from '../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledDatePicker from '../styled/StyledDatePicker';
import { zodResolver } from '@hookform/resolvers/zod';
import { EstimateInfoResponse } from '@/types/estimates/estimateInfoResponse';
import { useTranslation } from '@/i18n';
import { z } from 'zod';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';
import EstimateAgencyFlexibilityConfig from './EstimateAgencyFlexibilityConfig';

interface EstimateCreationInformationStepProps {
  circuitId: string | null;
  projectId: string;
  clientId: string;
  control: Control<CreateEstimateFormData>;
  estimateInfo?: EstimateInfoResponse;
  fromEditableCircuit?: boolean;
}

const EstimateCreationInformationStep = ({
  circuitId,
  projectId,
  clientId,
  control,
  estimateInfo,
  fromEditableCircuit = false,
}: EstimateCreationInformationStepProps) => {
  const { t } = useTranslation();
  const {
    data: circuitResponse,
    isSuccess: isCircuitSuccess,
    isLoading: isCircuitLoading,
  } = useGetCircuit(
    circuitId ?? '',
    false,
    true,
    !fromEditableCircuit && !!circuitId,
    { signImages: 'mainOnly', skipTranslations: true, pricingPreview: true },
  );
  const {
    data: editableCircuitResponse,
    isSuccess: isEditableCircuitSuccess,
    isLoading: isEditableCircuitLoading,
  } = useGetDistributorEditableCircuit(circuitId ?? '', fromEditableCircuit);
  const {
    data: projectResponse,
    isSuccess: isProjectSuccess,
    isLoading: isProjectLoading,
  } = useFindProject(clientId, projectId);

  if (!fromEditableCircuit && !circuitId) {
    return <p className="text-sm text-gray-700">{t('common.errorOccurred')}</p>;
  }

  return isCircuitLoading || isProjectLoading || isEditableCircuitLoading ? (
    <Loader />
  ) : !fromEditableCircuit ? (
    isCircuitSuccess &&
    isProjectSuccess && (
      <EstimateCreationInformationStepForm
        circuit={circuitResponse}
        project={projectResponse}
        control={control}
        estimateInfo={estimateInfo}
      />
    )
  ) : (
    isEditableCircuitSuccess &&
    isProjectSuccess && (
      <EstimateCreationInformationStepForm
        circuit={editableCircuitResponse}
        project={projectResponse}
        control={control}
        estimateInfo={estimateInfo}
      />
    )
  );
};

export default EstimateCreationInformationStep;

interface EstimateCreationInformationStepFormProps {
  project: ProjectResponse;
  circuit: CircuitResponse;
  control: Control<CreateEstimateFormData>;
  estimateInfo?: EstimateInfoResponse;
}

const EstimateCreationInformationStepForm = ({
  circuit,
  control,
  project,
  estimateInfo,
}: EstimateCreationInformationStepFormProps) => {
  const { t } = useTranslation();
  const { isTourOperator } = useDistributorLabels();
  const {
    field: { onChange: onChangeAdults },
  } = useController({ control, name: 'adults' });
  const {
    field: { onChange: onChangeChildren },
  } = useController({ control, name: 'children' });

  const defaultAdults = useMemo(
    () =>
      (estimateInfo && estimateInfo.adults.length > 0
        ? estimateInfo.adults
        : project.adults
      ).map((adult) => ({
        firstName: adult.firstName ?? undefined,
        lastName: adult.lastName ?? undefined,
        isSelected: true,
      })),
    [estimateInfo, project.adults],
  );

  const defaultChildren = useMemo(
    () =>
      (estimateInfo?.children ?? project.children).map((child) => ({
        firstName: child.firstName ?? undefined,
        lastName: child.lastName ?? undefined,
        birthDate: child.birthDate ? new Date(child.birthDate) : undefined,
        isSelected: true,
      })),
    [estimateInfo?.children, project.children],
  );

  useEffect(() => {
    onChangeAdults(defaultAdults);
    onChangeChildren(defaultChildren);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adults = useWatch({
    control,
    name: 'adults',
    defaultValue: defaultAdults,
  });
  const children = useWatch({
    control,
    name: 'children',
    defaultValue: defaultChildren,
  });

  const selectedAdults = useMemo(
    () => (adults ?? []).filter(({ isSelected }) => isSelected),
    [adults],
  );
  const selectedChildren = useMemo(
    () => (children ?? []).filter(({ isSelected }) => isSelected),
    [children],
  );

  const computeThisCircuitStartingPrice = useCallback(
    ({ nbAdults, nbChildren }: { nbAdults: number; nbChildren: number }) => {
      const nbParticipants = nbAdults + nbChildren;
      if (nbParticipants === 0) return 0;

      return (
        computeCircuitStartingPrice({
          circuitDays: circuit.circuitDays ?? [],
          nbParticipants,
          circuitMarginRate: circuit.marginRate,
          nbChildren,
          t,
        }) / nbParticipants
      );
    },
    [circuit.circuitDays, circuit.marginRate, t],
  );

  const circuitPrice = useMemo(
    () =>
      computeThisCircuitStartingPrice({
        nbAdults: selectedAdults.length,
        nbChildren: selectedChildren.length,
      }),
    [
      computeThisCircuitStartingPrice,
      selectedAdults.length,
      selectedChildren.length,
    ],
  );

  const [isAdultAdditionAreaOpen, setIsAdultAdditionAreaOpen] = useState(false);
  const [isChildrenAdditionAreaOpen, setIsChildrenAdditionAreaOpen] =
    useState(false);

  const { append: appendAdult } = useFieldArray({ control, name: 'adults' });
  const { append: appendChild } = useFieldArray({ control, name: 'children' });

  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="flex w-full gap-4">
        {circuit.mainImage && (
          <img className="w-36 h-auto rounded-lg" src={circuit.mainImage} />
        )}
        <div className="flex justify-between items-center w-full py-2">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-brand-600">
              {circuit.countries.join(', ')}
            </span>
            <h1 className="text-2xl leading-8 font-semibold text-gray-950">
              {circuit.name}
            </h1>
            <span className="text-sm font-normal text-black">
              {t('circuits.duration', {
                days: circuit.durationDays,
                nights: circuit.durationNights,
              })}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-normal text-gray-600">
              {t('common.starting_from')}
            </span>
            <h1 className="text-2xl font-bold text-brand-600">{`${formatPrice(circuitPrice)} € TTC`}</h1>
            <span className="text-sm font-normal text-brand-600">
              {t('common.perPerson')}
            </span>
          </div>
        </div>
      </div>

      {isTourOperator && <EstimateAgencyFlexibilityConfig control={control} />}

      <StyledInput control={control} name="name" title={t('estimates.name')} />

      <MarginRateStyledInput
        control={control}
        name="marginRate"
        title={t('estimates.marginRateDescription')}
      />

      <div className="flex flex-col gap-4 max-h-[500px] overflow-y-auto">
        <Label className="text-sm font-medium text-gray-700">
          {t('estimates.participants')}
        </Label>
        <div className="flex flex-col gap-2">
          <EstimateParticipantsList control={control} type="adults" />
          <EstimateParticipantsList control={control} type="children" />
        </div>
        <div className="flex gap-10">
          <StyledButton
            title={t('forms.labels.adults')}
            variant="link"
            className="p-0 text-brand-600"
            icon={<PlusIcon className="size-5 fill-brand-600" />}
            onClick={() => {
              setIsAdultAdditionAreaOpen((prev) => !prev);
            }}
          />
          <StyledButton
            title={t('forms.labels.children')}
            variant="link"
            className="p-0 text-brand-600"
            icon={<PlusIcon className="size-5 fill-brand-600" />}
            onClick={() => {
              setIsChildrenAdditionAreaOpen((prev) => !prev);
            }}
          />
        </div>
        {isAdultAdditionAreaOpen && (
          <AddParticipantForm
            type="adults"
            append={appendAdult}
            closeArea={() => setIsAdultAdditionAreaOpen(false)}
          />
        )}
        {isChildrenAdditionAreaOpen && (
          <AddParticipantForm
            type="children"
            append={appendChild}
            closeArea={() => setIsChildrenAdditionAreaOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

interface EstimateParticipantsListProps {
  control: Control<CreateEstimateFormData>;
  type: 'adults' | 'children';
}

const EstimateParticipantsList = ({
  control,
  type,
}: EstimateParticipantsListProps) => {
  const { t } = useTranslation();
  const participants = useWatch({
    control,
    name: type,
  });

  const label = useMemo(
    () =>
      type === 'adults'
        ? t('estimates.details.participants.adult')
        : t('estimates.details.participants.child'),
    [type, t],
  );

  return (
    <>
      {(participants ?? []).map((field, index) => (
        <div
          key={`participant-${type}-${index}`}
          className="flex gap-2 items-center"
        >
          <StyledBooleanCheckbox
            control={control}
            name={`${type}.${index}.isSelected`}
            optionLabel={`${label} ${index + 1} - ${field.lastName} ${field.firstName}`}
          />
        </div>
      ))}
    </>
  );
};

interface AddParticipantFormProps {
  type: 'adults' | 'children';
  closeArea: () => void;
  append: (value: EstimateParticipantFormData) => void;
}

const AddAdultParticipantSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  isSelected: z.boolean().default(true),
  birthDate: z.date().optional(),
});

const AddChildParticipantSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  isSelected: z.boolean().default(true),
  birthDate: z.date(),
});

const AddParticipantForm = ({
  type,
  closeArea,
  append,
}: AddParticipantFormProps) => {
  const { t } = useTranslation();
  const formSchema =
    type === 'adults' ? AddAdultParticipantSchema : AddChildParticipantSchema;
  const { control: FieldControl, handleSubmit } =
    useForm<EstimateParticipantFormData>({
      mode: 'onChange',
      resolver: zodResolver(formSchema),
      defaultValues: {
        firstName: undefined,
        lastName: undefined,
        isSelected: true,
        birthDate: undefined,
      },
    });

  const getLabel = useCallback(
    (input: 'Nom' | 'Prénom' | 'Date de naissance') => {
      if (type === 'adults')
        return `${t('estimates.details.participants.adult')} - ${input}`;
      return `${t('estimates.details.participants.child')} - ${input}`;
    },
    [type, t],
  );

  const addParticipant: SubmitHandler<EstimateParticipantFormData> =
    useCallback(
      (data) => {
        const normalizedFirstName = data.firstName?.trim() || 'Prénom';
        const normalizedLastName = data.lastName?.trim() || 'Nom';
        if (type === 'adults' || data.birthDate) {
          append({
            ...data,
            firstName: normalizedFirstName,
            lastName: normalizedLastName,
          });
          closeArea();
        }
      },
      [append, closeArea, type],
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-4 w-full">
        <StyledInput
          control={FieldControl}
          name={`lastName`}
          title={getLabel('Nom')}
          className="w-full"
        />
        <StyledInput
          control={FieldControl}
          name={`firstName`}
          title={getLabel('Prénom')}
          className="w-full"
        />
        {type === 'children' && (
          <div className="flex flex-col gap-1.5 w-full">
            <Label className="text-gray-700">
              {getLabel('Date de naissance')} *
            </Label>
            <StyledDatePicker
              control={FieldControl}
              name={`birthDate`}
              className="w-full"
              acceptOnlyPastDates
            />
          </div>
        )}
      </div>
      <StyledButton
        title={t('common.add')}
        onClick={handleSubmit(addParticipant)}
      />
    </div>
  );
};
